// api
import type { ReadUnitDto } from '@/api/generated/recalc/endpoints/v1/units';
import type {
  AlarmTypes,
  AssociationsDto,
  DataStreamAssociation,
  GetAlarmConfigurationDto,
  PostAlarmConfigurationDto,
  PutAlarmConfigurationDto,
  Severity,
} from '@/api/generated/recalc/endpoints/v2/alarmConfiguration';

// relative
import {
  buildAssociationsPayload,
  getDataStreamAssociationsFromKeys,
} from '../dataStreamUtils';
import {
  buildDefaultConfiguration,
  isObject,
  sanitizeForApi,
} from '../schemaUtils';
import type { DataStreamOption, JsonSchemaNode } from '../types';
import type { AlarmConfigurationFormValues } from './alarmConfigurationForm.schema';

/**
 * Matches an existing alarm configuration's saved `associations.dataStreams`
 * back to the `DataStreamOption` keys the associations selector understands,
 * so the create/edit forms don't have to duplicate this matching logic.
 */
export function toInitialSelectedDataStreamKeys(
  existingDataStreams: DataStreamAssociation[],
  dataStreamOptions: DataStreamOption[],
): string[] {
  return existingDataStreams
    .map((existingDataStream) => {
      const matchedDataStreamOption = dataStreamOptions.find(
        (option) =>
          option.unitId === existingDataStream.unitId &&
          option.dataType === existingDataStream.dataType &&
          (existingDataStream.unitTypeInputId == null ||
            option.unitTypeInputId === existingDataStream.unitTypeInputId),
      );

      return matchedDataStreamOption?.key;
    })
    .filter((key): key is string => Boolean(key));
}

export function toAlarmConfigurationFormValues(
  alarmConfiguration: GetAlarmConfigurationDto | undefined,
  resolvedSchema: JsonSchemaNode | null,
  defaultAlarmSubtype: string,
  initialSelectedDataStreamKeys: string[],
): AlarmConfigurationFormValues {
  const configuration = isObject(alarmConfiguration?.configurations)
    ? alarmConfiguration.configurations
    : resolvedSchema
      ? buildDefaultConfiguration(resolvedSchema)
      : {};

  return {
    title: alarmConfiguration?.title ?? '',
    message: alarmConfiguration?.message ?? '',
    alarmSubtype: alarmConfiguration?.alarmSubtype ?? defaultAlarmSubtype,
    severity: (alarmConfiguration?.severity ?? 'Medium') as Severity,
    dataStreamKeys: initialSelectedDataStreamKeys,
    configuration,
  };
}

export function toAlarmConfigurationAssociations(
  typedUnits: ReadUnitDto[],
  dataStreamOptions: DataStreamOption[],
  dataStreamKeys: string[],
): AssociationsDto {
  const selectedDataStreams = getDataStreamAssociationsFromKeys(
    dataStreamOptions,
    dataStreamKeys,
  );

  return buildAssociationsPayload(typedUnits, selectedDataStreams);
}

export function toPostAlarmConfigurationDto(
  values: AlarmConfigurationFormValues,
  accountId: number,
  alarmType: AlarmTypes,
  associations: AssociationsDto,
): PostAlarmConfigurationDto {
  return {
    accountId,
    title: values.title || null,
    message: values.message || null,
    alarmSubtype: values.alarmSubtype || null,
    severity: values.severity,
    alarmType,
    configurations: sanitizeForApi(values.configuration) as object,
    associations,
  };
}

export function toPutAlarmConfigurationDto(
  values: AlarmConfigurationFormValues,
  alarmType: AlarmTypes,
  associations: AssociationsDto,
): PutAlarmConfigurationDto {
  return {
    title: values.title || null,
    message: values.message || null,
    alarmSubtype: values.alarmSubtype || null,
    severity: values.severity,
    alarmType,
    configurations: sanitizeForApi(values.configuration) as object,
    associations,
  };
}
